import React, { useEffect, useRef, useState } from 'react';
import { User, MessageSquare, Maximize2, Minimize2, Volume2, VolumeX, Volume1, MicOff, Plus, Monitor } from 'lucide-react';

interface VideoTileProps {
  participant: any;
  isSpeaking: boolean;
  stream: MediaStream | null;
  onContextMenu: (e: React.MouseEvent, userId: string) => void;
  onSendMessage?: (userId: string) => void;
  hideName?: boolean;
  large?: boolean;
  isLocal?: boolean;
  nameStyle?: React.CSSProperties;
  isCinemaMode?: boolean;
  onToggleCinemaMode?: () => void;
}

// V1 (CSS-blur on the local <video> element) is gone — the V2 canvas
// pipeline in services/videoProcessing.ts owns the entire blur + virtual-
// background effect chain now and produces a derived MediaStream that
// already flows through this component without any per-tile styling.
// Whichever stream the parent passes to `stream` is the final pixel the
// user sees — no further transform is needed here.
export default function VideoTile({
  participant,
  isSpeaking,
  stream,
  onContextMenu,
  onSendMessage,
  hideName = false,
  large = false,
  isLocal = false,
  nameStyle,
  isCinemaMode,
  onToggleCinemaMode
}: VideoTileProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [volume, setVolume] = useState(1.0);
  const [isMuted, setIsMuted] = useState(false); // Don't default to muted — audio must play
  const [isVideoTrackEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioTrackEnabled, setIsAudioEnabled] = useState(true);
  const [videoTrackId, setVideoTrackId] = useState<string | null>(null);
  const [audioTrackId, setAudioTrackId] = useState<string | null>(null);
  const [isWatchingScreen, setIsWatchingScreen] = useState(isLocal || large);

  useEffect(() => {
    if (large || isLocal) setIsWatchingScreen(true);
  }, [large, isLocal]);

  // Monitor track enabled states (enabled property doesn't trigger re-renders)
  const videoTrackStateRef = useRef({ enabled: false, id: null as string | null });
  const audioTrackStateRef = useRef({ enabled: false, id: null as string | null });

  useEffect(() => {
    if (!stream) return;

    const videoTrack = stream.getVideoTracks()[0];
    const audioTrack = stream.getAudioTracks()[0];

    const checkTracks = () => {
      const isVideoEnabled = videoTrack ? videoTrack.enabled && videoTrack.readyState === 'live' : false;
      if (isVideoEnabled !== videoTrackStateRef.current.enabled) {
        videoTrackStateRef.current.enabled = isVideoEnabled;
        setIsVideoEnabled(isVideoEnabled);
      }
      if (videoTrack?.id !== videoTrackStateRef.current.id) {
        videoTrackStateRef.current.id = videoTrack?.id || null;
        setVideoTrackId(videoTrack?.id || null);
      }

      const isAudioEnabled = audioTrack ? audioTrack.enabled && audioTrack.readyState === 'live' : false;
      if (isAudioEnabled !== audioTrackStateRef.current.enabled) {
        audioTrackStateRef.current.enabled = isAudioEnabled;
        setIsAudioEnabled(isAudioEnabled);
      }
      if (audioTrack?.id !== audioTrackStateRef.current.id) {
        audioTrackStateRef.current.id = audioTrack?.id || null;
        setAudioTrackId(audioTrack?.id || null);
      }
    };

    // Run immediately on stream change
    checkTracks();

    // Subscribe to track lifecycle events for instant updates
    // instead of relying solely on polling.
    const handleTrackEnded = () => checkTracks();
    videoTrack?.addEventListener('ended', handleTrackEnded);
    audioTrack?.addEventListener('ended', handleTrackEnded);
    // 'mute'/'unmute' fire on MediaStreamTrack when the UA disables/enables
    videoTrack?.addEventListener('mute', handleTrackEnded);
    videoTrack?.addEventListener('unmute', handleTrackEnded);
    audioTrack?.addEventListener('mute', handleTrackEnded);
    audioTrack?.addEventListener('unmute', handleTrackEnded);

    // Fallback polling at 2s (was 500ms) — catches programmatic .enabled
    // toggles that don't fire mute/unmute events
    const interval = setInterval(checkTracks, 2000);

    return () => {
      clearInterval(interval);
      videoTrack?.removeEventListener('ended', handleTrackEnded);
      audioTrack?.removeEventListener('ended', handleTrackEnded);
      videoTrack?.removeEventListener('mute', handleTrackEnded);
      videoTrack?.removeEventListener('unmute', handleTrackEnded);
      audioTrack?.removeEventListener('mute', handleTrackEnded);
      audioTrack?.removeEventListener('unmute', handleTrackEnded);
    };
  }, [stream]);

  useEffect(() => {
    if (videoRef.current) {
      if (stream) {
        if (videoRef.current.srcObject !== stream) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(err => {
            console.warn('[VideoTile] Autoplay prevented, waiting for interaction:', err);
          });
        }
      } else {
        videoRef.current.srcObject = null;
      }
    }
  }, [stream, isLocal]);

  useEffect(() => {
    if (videoRef.current) {
      // Local video is ALWAYS muted in the UI to prevent feedback
      // Remote video's mute state is controlled by isMuted state
      videoRef.current.muted = isLocal || isMuted;
      videoRef.current.volume = isLocal ? 0 : volume;
      
      if (!videoRef.current.muted) {
          // If we unmute a video, we might need to call play again
          videoRef.current.play().catch(() => {});
      }
    }
  }, [volume, isMuted, isLocal]);

  const toggleFullscreen = () => {
    if (onToggleCinemaMode) {
      onToggleCinemaMode();
    } else if (containerRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        containerRef.current.requestFullscreen();
      }
    }
  };

  if (participant.type === 'screen') {
    if (!isWatchingScreen) {
      return (
        <div className="w-full h-full relative bg-black/90 rounded-2xl overflow-hidden border-2 border-[var(--neon)] shadow-[0_0_20px_var(--neon)] group flex flex-col items-center justify-center animate-pulse-slow">
           <div className="flex flex-col items-center gap-3">
             <span className="text-xs font-bold text-[var(--neon)] uppercase tracking-widest text-center px-4">{participant.name} is sharing their screen</span>
             <div className="flex items-center gap-2">
                <button 
                  onClick={(e) => {
                     e.stopPropagation();
                     // We trigger the same logic as the plus button for now, but visually it's the main call to action
                     setIsWatchingScreen(true);
                  }}
                  className="px-4 py-2 bg-[var(--neon)] text-black text-xs font-bold rounded-lg hover:bg-white hover:scale-105 transition-all shadow-[0_0_10px_var(--neon)]"
                >
                  Join Screenshare
                </button>
                <button 
                  onClick={(e) => {
                     e.stopPropagation();
                     setIsWatchingScreen(true);
                  }}
                  className="w-8 h-8 flex items-center justify-center bg-white/10 text-white rounded-lg hover:bg-white/20 hover:scale-105 transition-all"
                  title="Watch here"
                >
                  <Plus size={14} />
                </button>
             </div>
           </div>
        </div>
      );
    }

    return (
      <div 
        ref={containerRef}
        className="w-full h-full relative bg-black rounded-2xl overflow-hidden border-2 border-[var(--neon)] shadow-[0_0_20px_var(--neon)] animate-pulse-slow group"
      >
        {/* Permanent label at top-left */}
        <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-2 py-1 rounded-md bg-black/60 backdrop-blur-sm border border-[var(--neon)]/30 text-[9px] font-black text-[var(--neon)] uppercase tracking-wider">
          <Monitor size={10} className="animate-pulse" />
          <span>{participant.name} is sharing their screen</span>
        </div>

        <video 
          ref={videoRef} 
          autoPlay 
          playsInline 
          muted={isMuted} 
          className="w-full h-full object-contain" 
        />
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
           <div className="absolute top-3 right-3 flex items-center gap-2">
            <div className="flex items-center gap-2 bg-black/40 backdrop-blur-md rounded-lg p-1 border border-white/10">
              <div className="flex items-center gap-1.5 px-2 border-r border-white/10">
                <button 
                  onClick={(e) => { e.stopPropagation(); setIsMuted(!isMuted); }}
                  className="text-white/60 hover:text-[var(--neon)] transition-colors"
                >
                  {isMuted || volume === 0 ? <VolumeX size={14} /> : volume < 0.5 ? <Volume1 size={14} /> : <Volume2 size={14} />}
                </button>
                <input 
                  type="range" 
                  min="0" 
                  max="1" 
                  step="0.01"
                  value={volume}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    setVolume(parseFloat(e.target.value));
                    if (parseFloat(e.target.value) > 0) setIsMuted(false);
                  }}
                  className="w-16 h-1 bg-white/20 rounded-full appearance-none cursor-pointer accent-[var(--neon)]"
                />
              </div>
              <button 
                onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }}
                className="p-1 text-white/60 hover:text-[var(--neon)] transition-colors"
                title="Fullscreen"
              >
                {isCinemaMode ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
              </button>
            </div>
          </div>

          {!hideName && (
            <div className="absolute bottom-2 left-2 px-2 py-1 bg-[var(--neon)] rounded-lg text-[10px] font-bold text-black uppercase flex items-center gap-2">
              {participant.id === 'screen' ? 'SCREEN SHARE' : 'YOUR SCREEN'}
            </div>
          )}
        </div>
      </div>
    );
  }

  const hasVideoTrack = stream &&
                        stream.getVideoTracks().length > 0 &&
                        isVideoTrackEnabled &&
                        (participant.type === 'screen' || isLocal || participant.isCameraOn);

  return (
    <div 
      className={`w-full h-full relative rounded-2xl overflow-hidden border transition-all duration-300 group ${isSpeaking ? 'border-emerald-400 ring-2 ring-emerald-400/30 shadow-[0_0_20px_rgba(52,211,153,0.3)]' : 'border-white/10'}`}
      onContextMenu={(e) => onContextMenu(e, participant.id)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Remote Mute Indicator */}
      {!isAudioTrackEnabled && !isLocal && (
        <div className="absolute top-4 right-4 bg-red-500/90 text-white p-1.5 rounded-full z-20 shadow-lg">
          <MicOff size={14} />
        </div>
      )}

      {stream && (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={isLocal}
          className={`w-full h-full relative z-10 ${large ? 'object-contain bg-black/40' : 'object-cover'} ${hasVideoTrack ? 'block' : 'absolute pointer-events-none opacity-0 w-1 h-1'}`}
        />
      )}

      {!hasVideoTrack && (
        <div className="w-full h-full flex items-center justify-center bg-black/50 absolute inset-0 z-0 overflow-hidden">
          {participant.avatar ? (
            <>
              {/* Blurred background */}
              <img src={participant.avatar} className="absolute inset-0 w-full h-full object-cover blur-3xl opacity-20 scale-125" referrerPolicy="no-referrer" />
              {/* Centered avatar with speaking glow */}
              <div className={`relative z-10 rounded-full overflow-hidden shadow-2xl transition-all duration-300 ${large ? 'w-48 h-48' : 'w-24 h-24'} ${isSpeaking ? 'border-4 border-emerald-400 shadow-[0_0_30px_rgba(74,222,128,0.6)]' : 'border-4 border-white/10'}`}>
                <img src={participant.avatar} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
              </div>
              {isSpeaking && (
                <div className={`absolute z-10 rounded-full border-2 border-emerald-400/40 animate-ping pointer-events-none ${large ? 'w-52 h-52' : 'w-28 h-28'}`} />
              )}
            </>
          ) : (
            <div className={`relative z-10 rounded-full bg-white/5 flex items-center justify-center shadow-2xl ${large ? 'w-48 h-48' : 'w-24 h-24'} ${isSpeaking ? 'border-4 border-emerald-400 shadow-[0_0_30px_rgba(74,222,128,0.6)]' : 'border-4 border-white/10'}`}>
              <User size={large ? 80 : 40} className="text-white/20" />
            </div>
          )}
        </div>
      )}
      
      {!hideName && (
        <div className={`absolute bottom-2 left-2 px-2 py-1 bg-black/60 backdrop-blur-md rounded-lg text-[10px] font-bold uppercase flex items-center gap-2 border transition-all duration-300 ${isSpeaking ? 'border-emerald-400/50 text-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.2)]' : 'border-white/10 text-white'}`}>
          {isSpeaking && (
            <div className="relative w-2 h-2 flex items-center justify-center shrink-0">
              <span className="absolute w-2 h-2 bg-emerald-500 rounded-full animate-ping" />
              <span className="absolute w-1.5 h-1.5 bg-emerald-500 rounded-full" />
            </div>
          )}
          <span style={nameStyle}>{participant.name}</span>
        </div>
      )}

      {isHovered && participant.id !== 'me' && (
        <div className="absolute inset-0 bg-black/60 backdrop-blur-md flex flex-col items-center justify-center gap-2 animate-in fade-in duration-200 z-20">
          <button 
            className="px-4 py-2 bg-[var(--neon)] text-black text-xs font-bold rounded-lg hover:scale-105 transition-transform flex items-center gap-2 shadow-[0_0_20px_hsla(var(--h),var(--sat),var(--lit),0.3)]"
            onClick={(e) => {
              e.stopPropagation();
              if (onSendMessage) onSendMessage(participant.id);
            }}
          >
            <MessageSquare size={14} />
            Send Message
          </button>
          
          <div className="flex items-center gap-2 bg-black/60 backdrop-blur-md rounded-lg p-2 border border-white/10 mt-2">
            <button 
              onClick={(e) => { e.stopPropagation(); setIsMuted(!isMuted); }}
              className="text-white/60 hover:text-[var(--neon)] transition-colors"
            >
              {isMuted || volume === 0 ? <VolumeX size={14} /> : volume < 0.5 ? <Volume1 size={14} /> : <Volume2 size={14} />}
            </button>
            <input 
              type="range" 
              min="0" 
              max="1" 
              step="0.01"
              value={volume}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                setVolume(parseFloat(e.target.value));
                if (parseFloat(e.target.value) > 0) setIsMuted(false);
              }}
              className="w-20 h-1.5 bg-white/20 rounded-full appearance-none cursor-pointer accent-[var(--neon)]"
            />
          </div>

          <span className="text-[10px] text-white/50 uppercase tracking-widest font-bold mt-1">Right-click for profile</span>
        </div>
      )}
    </div>
  );
}
